import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Car, 
  MapPin, 
  Building2, 
  Users, 
  LogOut,
  Menu,
  X,
  QrCode,
  FileText,
  AlertTriangle,
  Settings,
  TrendingUp,
  MessageSquare
} from "lucide-react";

export const Sidebar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const menuItems = [
    { icon: TrendingUp, label: "Dashboard", path: "/dashboard", roles: ["super_admin", "gestor_local", "gestor_empresa", "operador_parque"] },
    { icon: QrCode, label: "Scanner", path: "/scanner", roles: ["super_admin", "gestor_local", "operador_parque"] },
    { icon: MapPin, label: "Locais", path: "/locais", roles: ["super_admin"] },
    { icon: Building2, label: "Empresas", path: "/empresas", roles: ["super_admin", "gestor_local"] },
    { icon: Settings, label: "Quotas", path: "/quotas", roles: ["super_admin", "gestor_local"] },
    { icon: Car, label: "Viaturas", path: "/viaturas", roles: ["super_admin", "gestor_local", "gestor_empresa"] },
    { icon: Users, label: "Utilizadores", path: "/users", roles: ["super_admin", "gestor_local", "gestor_empresa"] },
    { icon: FileText, label: "Movimentos", path: "/movimentos", roles: ["super_admin", "gestor_local", "gestor_empresa"] },
    { icon: AlertTriangle, label: "Incidências", path: "/incidencias", roles: ["super_admin", "gestor_local", "gestor_empresa"] },
    { icon: MessageSquare, label: "Contactos", path: "/contactos", roles: ["super_admin"] },
  ];

  const filteredItems = menuItems.filter(item => item.roles.includes(user?.role));

  const roleLabels = {
    super_admin: "Super Admin",
    gestor_local: "Gestor Local",
    gestor_empresa: "Gestor Empresa",
    operador_parque: "Operador"
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-white border-r border-[#E2E8F0]
        transform transition-transform duration-200 ease-out
        lg:translate-x-0 lg:static lg:z-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#E2E8F0]">
            <div className="flex items-center justify-between">
              <Link to="/dashboard" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0047FF] rounded-lg flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-[#0F172A] tracking-tight">GAPE</span>
              </Link>
              <button 
                onClick={() => setIsOpen(false)}
                className="lg:hidden p-2 text-[#64748B] hover:text-[#0F172A]"
                data-testid="close-sidebar-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-1">
              {filteredItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-150 ${
                      isActive 
                        ? 'bg-[#0047FF]/10 text-[#0047FF]' 
                        : 'text-[#64748B] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
                    }`}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-[#E2E8F0]">
            <div className="px-4 py-3 rounded-lg bg-[#F8FAFC]">
              <p className="font-medium text-[#0F172A] truncate">{user?.nome}</p>
              <p className="text-sm text-[#64748B] truncate">{user?.email}</p>
              <Badge variant="secondary" className="mt-2 text-xs">
                {roleLabels[user?.role] || user?.role}
              </Badge>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full mt-3 justify-start text-[#64748B] hover:text-[#D50000] hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Terminar Sessão
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export const PageLayout = ({ children, title, subtitle, headerAction }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <main className="flex-1 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-[#64748B] hover:text-[#0F172A]"
                data-testid="open-sidebar-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-[#0F172A]">{title}</h1>
                {subtitle && <p className="text-sm text-[#64748B]">{subtitle}</p>}
              </div>
            </div>
            {headerAction}
          </div>
        </header>

        {/* Content */}
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default PageLayout;
